import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, ShieldCheck, Building2, Hash, Landmark, Globe, Rocket } from 'lucide-react';
// ─────────────────────────────────────────────────────────────
// Floating editorial cards — used in both mobile & desktop visuals
// ─────────────────────────────────────────────────────────────
type FloatCardProps = {
  icon: React.ReactNode;
  label: string;
  sub?: string;
  status?: 'done' | 'pending' | 'final';
  className?: string;
  delay?: number;
};
function FloatCard({
  icon,
  label,
  sub,
  status = 'done',
  className = '',
  delay = 0
}: FloatCardProps) {
  const isFinal = status === 'final';
  const isPending = status === 'pending';
  return <motion.div initial={{
    opacity: 0,
    y: 12,
    scale: 0.96
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} transition={{
    duration: 0.6,
    delay,
    ease: [0.22, 1, 0.36, 1]
  }} className={`${className} ${isFinal ? 'bg-ink text-white border-ink/0' : 'bg-white text-ink border-ink/5'} rounded-2xl px-4 py-3 shadow-soft border backdrop-blur-sm flex items-center gap-3`} data-id="element-57">
      <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${isFinal ? 'bg-accent-light/30 text-white' : 'bg-lavender text-accent'}`} data-id="element-58">
        {icon}
      </div>
      <div className="min-w-0" data-id="element-59">
        <p className={`text-sm font-bold leading-tight ${isFinal ? 'text-white' : 'text-ink'}`} data-id="element-60">
          {label}
        </p>
        {sub && <p className={`text-[11px] font-medium leading-tight mt-0.5 ${isFinal ? 'text-white/60' : 'text-ink/55'}`} data-id="element-61">
            {sub}
          </p>}
      </div>
      {!isFinal && <span className={`flex-shrink-0 w-2 h-2 rounded-full ${isPending ? 'bg-ink/15' : 'bg-accent'}`} data-id="element-62" />}
    </motion.div>;
}
export function Hero() {
  return <section className="relative overflow-hidden" data-id="element-63">
      {/* Shared ambient background — lavender glows */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-64">
        <div className="absolute top-[-10%] right-[-10%] w-[55%] h-[55%] bg-accent-light/25 blur-[120px] rounded-full" data-id="element-65" />
        <div className="absolute bottom-[-15%] left-[-10%] w-[45%] h-[45%] bg-accent/10 blur-[120px] rounded-full" data-id="element-66" />
      </div>

      {/* ───────────────────────── MOBILE (< md) ───────────────────────── */}
      <div className="md:hidden relative px-6 pt-10 pb-16" data-id="element-67">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="relative z-10 max-w-md mx-auto" data-id="element-68">
          {/* Eyebrow / Badge */}
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-ink/10 shadow-sm mb-6" data-id="element-69">
            <Sparkles className="w-3.5 h-3.5 text-accent" data-id="element-70" />
            <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-ink" data-id="element-71">
              Ebook gratuito
            </span>
          </div>

          {/* Headline */}
          <h1 className="font-serif text-[2.15rem] font-bold text-ink leading-[1.1] tracking-tight mb-4" data-id="element-72">
            Tu idea no necesita más tiempo.{' '}
            <em className="italic text-accent-light" data-id="element-73">Necesita estructura.</em>
          </h1>

          {/* Subheadline */}
          <p className="text-[15px] text-ink/65 leading-[1.55] mb-8" data-id="element-74">
            Descarga gratis{' '}
            <span className="font-serif italic text-ink font-semibold" data-id="element-75">
              "El primer paso que nadie da"
            </span>
            , el ebook de Inkova para convertir tu idea en un negocio real en
            EE.UU. sin esperar el momento perfecto.
          </p>

          {/* CTA principal */}
          <a href="https://inkovacreation.store/shop/aaee3cf3-82b8-4d25-9f96-f42edc2ac2a0" target="_blank" rel="noopener noreferrer" className="group w-full bg-accent hover:bg-ink text-white px-6 py-4 rounded-full font-semibold text-[15px] transition-all shadow-soft flex items-center justify-center gap-2 active:scale-[0.98]" data-id="element-76">
            Quiero mi ebook gratuito
            <ArrowRight className="w-4.5 h-4.5 group-hover:translate-x-1 transition-transform" data-id="element-77" />
          </a>

          {/* Trust microcopy */}
          <div className="flex items-center justify-center gap-1.5 mt-3.5" data-id="element-78">
            <ShieldCheck className="w-3.5 h-3.5 text-accent/70" data-id="element-79" />
            <p className="text-[11.5px] font-medium text-ink/55 tracking-wide" data-id="element-80">
              Gratis · En español · Sin complicaciones
            </p>
          </div>

          {/* ─── Editorial visual: idea → estructura → negocio real ─── */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          delay: 0.3
        }} className="mt-12 relative" data-id="element-81">
            <div className="flex items-center justify-between mb-4" data-id="element-82">
              <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-ink/40" data-id="element-83">
                Tu camino
              </span>
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-accent" data-id="element-84">
                Idea → Negocio real
              </span>
            </div>

            {/* Composition canvas */}
            <div className="relative h-[460px] rounded-[2rem] bg-gradient-to-br from-white via-lavender-soft to-lavender border border-white/60 shadow-soft overflow-hidden" data-id="element-85">
              {/* Soft orb */}
              <div className="absolute top-8 right-6 w-44 h-44 rounded-full bg-gradient-to-br from-accent-light/40 to-accent/30 blur-2xl" data-id="element-86" />
              <div className="absolute bottom-10 left-8 w-32 h-32 rounded-full bg-white/60 blur-2xl" data-id="element-87" />

              {/* Dotted connecting path */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 320 420" fill="none" preserveAspectRatio="none" aria-hidden="true" data-id="element-88">
                <path d="M 50 60 Q 240 100, 90 200 T 230 360" stroke="#6B3FA0" strokeOpacity="0.25" strokeWidth="1.5" strokeDasharray="3 5" fill="none" data-id="element-89" />
              </svg>

              {/* Floating cards */}
              <FloatCard icon={<Building2 className="w-4 h-4" data-id="element-91" />} label="LLC lista" sub="Empresa formada" className="absolute top-6 left-5 w-[58%]" delay={0.5} data-id="element-90" />
              <FloatCard icon={<Hash className="w-4 h-4" data-id="element-93" />} label="EIN" sub="Identificación fiscal" className="absolute top-[28%] right-5 w-[58%]" delay={0.6} data-id="element-92" />
              <FloatCard icon={<Landmark className="w-4 h-4" data-id="element-95" />} label="Cuenta bancaria" sub="Activa" className="absolute top-[52%] left-5 w-[60%]" delay={0.7} data-id="element-94" />
              <FloatCard icon={<Globe className="w-4 h-4" data-id="element-97" />} label="Presencia digital" sub="Marca + canales" status="pending" className="absolute top-[70%] right-5 w-[58%]" delay={0.8} data-id="element-96" />
              <FloatCard icon={<Rocket className="w-4 h-4" data-id="element-99" />} label="Negocio real" sub="Lista para operar" status="final" className="absolute bottom-4 left-5 right-5" delay={0.95} data-id="element-98" />
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* ───────────────────────── DESKTOP (md+) ───────────────────────── */}
      <div className="hidden md:block pt-20 pb-28 lg:pt-28 lg:pb-36" data-id="element-100">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10" data-id="element-101">
          {/* Left Column: Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="lg:col-span-6 max-w-2xl" data-id="element-102">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-ink/10 shadow-sm mb-8" data-id="element-103">
              <Sparkles className="w-3.5 h-3.5 text-accent" data-id="element-104" />
              <span className="text-xs font-bold tracking-[0.18em] uppercase text-ink" data-id="element-105">
                Ebook gratuito
              </span>
            </div>

            <h1 className="font-serif text-5xl lg:text-[4.25rem] font-bold text-ink leading-[1.02] tracking-tight mb-7" data-id="element-106">
              Tu idea no necesita más tiempo.{' '}
              <em className="italic text-accent-light" data-id="element-107">Necesita estructura.</em>
            </h1>

            <p className="text-lg lg:text-xl text-ink/70 mb-10 leading-relaxed max-w-xl" data-id="element-108">
              Descarga gratis{' '}
              <span className="font-serif italic text-ink font-semibold" data-id="element-109">
                "El primer paso que nadie da"
              </span>
              , el ebook de Inkova para entender cómo convertir tu idea en un
              negocio real en EE.UU. sin esperar el momento perfecto.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-5" data-id="element-110">
              <a href="https://inkovacreation.store/shop/aaee3cf3-82b8-4d25-9f96-f42edc2ac2a0" target="_blank" rel="noopener noreferrer" className="group bg-accent hover:bg-ink text-white px-8 py-4 rounded-full font-semibold text-base lg:text-lg transition-all shadow-soft hover:shadow-xl inline-flex items-center gap-2.5 transform hover:-translate-y-0.5" data-id="element-111">
                Quiero mi ebook gratuito
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-112" />
              </a>
            </div>

            <div className="flex items-center gap-2" data-id="element-113">
              <ShieldCheck className="w-4 h-4 text-accent/70" data-id="element-114" />
              <p className="text-sm font-medium text-ink/60" data-id="element-115">
                Gratis · En español · Sin complicaciones
              </p>
            </div>
          </motion.div>

          {/* Right Column: Editorial visual composition */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.7,
          delay: 0.2
        }} className="lg:col-span-6 relative w-full" data-id="element-116">
            <div className="relative w-full aspect-square max-w-[560px] mx-auto" data-id="element-117">
              {/* Big soft orb */}
              <div className="absolute top-[10%] right-[10%] w-[70%] h-[70%] rounded-full bg-gradient-to-br from-accent-light/40 via-accent/25 to-accent/10 blur-3xl" data-id="element-118" />

              {/* Lavender base circle */}
              <div className="absolute top-[12%] left-[12%] w-[70%] h-[70%] rounded-full bg-gradient-to-br from-white/80 to-lavender border border-white/70 shadow-soft" data-id="element-119" />

              {/* Inner highlight ring */}
              <div className="absolute top-[24%] left-[24%] w-[45%] h-[45%] rounded-full border border-accent/15" data-id="element-120" />

              {/* Editorial label center */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none" data-id="element-121">
                <span className="block text-[10px] font-bold tracking-[0.25em] uppercase text-ink/40 mb-1" data-id="element-122">
                  Tu camino
                </span>
                <span className="block font-serif italic text-ink/70 text-lg" data-id="element-123">
                  Idea → Negocio real
                </span>
              </div>

              {/* Dotted connecting path */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 560 560" fill="none" preserveAspectRatio="none" aria-hidden="true" data-id="element-124">
                <path d="M 80 110 Q 320 80, 460 220 T 380 480 Q 200 540, 100 380" stroke="#6B3FA0" strokeOpacity="0.22" strokeWidth="1.5" strokeDasharray="4 6" fill="none" data-id="element-125" />
              </svg>

              {/* Floating cards arranged asymmetrically */}
              <FloatCard icon={<Building2 className="w-4 h-4" data-id="element-127" />} label="LLC lista" sub="Empresa formada" className="absolute top-[6%] left-[2%] w-[55%]" delay={0.4} data-id="element-126" />
              <FloatCard icon={<Hash className="w-4 h-4" data-id="element-129" />} label="EIN" sub="Identificación fiscal" className="absolute top-[28%] right-[-4%] w-[50%]" delay={0.55} data-id="element-128" />
              <FloatCard icon={<Landmark className="w-4 h-4" data-id="element-131" />} label="Cuenta bancaria" sub="Activa" className="absolute top-[54%] left-[-2%] w-[52%]" delay={0.7} data-id="element-130" />
              <FloatCard icon={<Globe className="w-4 h-4" data-id="element-133" />} label="Presencia digital" sub="Marca + canales" status="pending" className="absolute top-[72%] right-[2%] w-[52%]" delay={0.85} data-id="element-132" />
              <FloatCard icon={<Rocket className="w-4 h-4" data-id="element-135" />} label="Negocio real" sub="Lista para operar" status="final" className="absolute bottom-[1%] left-[2%] w-[48%]" delay={1.0} data-id="element-134" />

              {/* Subtle floating motion on whole stack */}
              <motion.div animate={{
              y: [0, -6, 0]
            }} transition={{
              duration: 6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="absolute -top-3 right-[8%] bg-white/90 backdrop-blur-sm border border-ink/5 rounded-full px-3 py-1.5 shadow-soft" data-id="element-136">
                <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-accent" data-id="element-137">
                  ✦ Estructura legal
                </span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}